import type { BasketItem, Product } from '../types/order';

/** Μια πρόταση πώλησης όπως τη στέλνει ο server στο ταμείο (ΤΟΠΙΚΑ id, με σειρά προτεραιότητας). */
export interface UpsellSuggestion {
  id: number;
  message: string;
  trigger_mode: 'always' | 'cart_contains';
  product_ids: number[];
  trigger_product_ids: number[];
  trigger_category_ids: number[];
}

export interface UpsellPick {
  suggestion: UpsellSuggestion;
  products: Product[];
}

/**
 * Η πρόταση που θα δει ο ταμίας στο «Ολοκλήρωση»: η ΠΡΩΤΗ (κατά προτεραιότητα) που
 * ταιριάζει στο καλάθι και έχει τουλάχιστον ένα προϊόν που μπορεί να μπει τώρα.
 *
 * Ένα προϊόν προτείνεται μόνο αν:
 *   • υπάρχει στον κατάλογο που έχει φορτώσει το ταμείο (διαθέσιμο, όχι κρυφό),
 *   • η κατηγορία του δεν είναι κρυφή,
 *   • ΔΕΝ είναι ήδη στο καλάθι (ο πελάτης το πήρε — δεν το ξαναπροτείνουμε),
 *   • δεν είναι τέλος, και το δέχεται το καλάθι (canAdd: π.χ. όχι υπηρεσία με προϊόντα).
 * «Μόνο αν το καλάθι έχει…» ταιριάζει με ΤΟΠΙΚΟ id προϊόντος ή κατηγορίας (ο server έχει
 * ήδη απλώσει την κατηγορία στις υποκατηγορίες της).
 */
export function pickUpsell(
  suggestions: UpsellSuggestion[],
  basket: BasketItem[],
  catalog: Product[],
  options: { canAdd?: (product: Product) => boolean; hiddenCategoryIds?: Set<number> } = {},
): UpsellPick | null {
  if (!suggestions.length || !basket.length) return null;
  const goods = basket.filter(item => item.product?.fee_category == null);
  if (goods.length === 0) return null;

  const inBasket = new Set(goods.map(item => Number(item.product.id)));
  const basketCategories = new Set(goods.map(item => Number(item.product.category_id)));
  const byId = new Map(catalog.map(p => [Number(p.id), p]));
  const hidden = options.hiddenCategoryIds || new Set<number>();
  const canAdd = options.canAdd || (() => true);

  for (const s of suggestions) {
    if (s.trigger_mode === 'cart_contains') {
      const hit = s.trigger_product_ids.some(id => inBasket.has(Number(id)))
        || s.trigger_category_ids.some(id => basketCategories.has(Number(id)));
      if (!hit) continue;
    }
    const products = s.product_ids
      .map(id => byId.get(Number(id)))
      .filter((p): p is Product => !!p
        && p.is_available !== false
        && !p.is_hidden
        && p.fee_category == null
        && !hidden.has(Number(p.category_id))
        && !inBasket.has(Number(p.id))
        && canAdd(p));
    if (products.length > 0) return { suggestion: s, products };
  }
  return null;
}
